
import React, { useState } from 'react';
import { FileText, Download, Eye, Upload, FilePlus, FileArchive, FileX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Patient } from '@/types/doctor';

interface PatientDocumentsProps {
  patient: Patient & {
    documents?: {
      id: string;
      type: 'xray' | 'photo' | 'report' | 'prescription' | 'other';
      name: string;
      date: string;
      url: string;
      size: string;
    }[];
  };
}

const PatientDocuments: React.FC<PatientDocumentsProps> = ({ patient }) => {
  const documents = patient.documents || [];
  const [viewDocument, setViewDocument] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Document type icons
  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'xray':
        return <FileArchive className="h-4 w-4 text-blue-600" />;
      case 'photo':
        return <FileArchive className="h-4 w-4 text-green-600" />;
      case 'report':
        return <FileText className="h-4 w-4 text-orange-600" />;
      case 'prescription':
        return <FileText className="h-4 w-4 text-purple-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };
  
  const openViewDialog = (url: string) => {
    setViewDocument(url);
    setViewDialogOpen(true);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Medical Documents ({documents.length})</h3>
        <div className="flex gap-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </Button>
          <Button>
            <FilePlus className="h-4 w-4 mr-2" />
            New Report
          </Button>
        </div>
      </div>
      
      {documents.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Type</th>
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Size</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-b last:border-b-0">
                      <td className="p-4">
                        <div className="flex items-center">
                          {getDocumentIcon(doc.type)}
                          <span className="ml-2">{doc.name}</span>
                        </div>
                      </td>
                      <td className="p-4 capitalize">{doc.type}</td>
                      <td className="p-4">{new Date(doc.date).toLocaleDateString()}</td>
                      <td className="p-4">{doc.size}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openViewDialog(doc.url)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <FileX className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="p-8 text-center border rounded-md bg-muted/10">
          <p className="text-muted-foreground mb-4">No documents uploaded yet</p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline">
              <Upload className="h-4 w-4 mr-2" />
              Upload File
            </Button>
            <Button>
              <FilePlus className="h-4 w-4 mr-2" />
              Create Report
            </Button>
          </div>
        </div>
      )}
      
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Document Viewer</DialogTitle>
          </DialogHeader>
          <div className="p-2 flex items-center justify-center">
            {viewDocument && (
              <iframe 
                src={viewDocument} 
                className="w-full h-[70vh]" 
                title="Document Viewer"
              ></iframe>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDocuments;
